import React, { useEffect, useState } from 'react';
import api from '../services/api';
import ComsErrorBanner from '../components/ComsErrorBanner';
import { RSMIN_APP_URL } from '../config';

const getChannelStatus = (channel, key, baseUrl) => {
  const label = channel?.spaceName || channel?.label || `${key[0].toUpperCase()}${key.slice(1)} Space`;
  const link = buildSpaceUrl(channel, baseUrl);

  if (!channel?.enabled) {
    return { key, label, severity: 'muted', status: 'Disabled', detail: 'Channel is not enabled.', link: '' };
  }
  if (!channel?.slug) {
    return { key, label, severity: 'warn', status: 'Missing mapping', detail: 'Channel is enabled but no slug is configured.', link: '' };
  }
  if (!channel?.spaceId) {
    return { key, label, severity: 'warn', status: 'Needs verification', detail: 'Slug exists, but canonical spaceId is not cached yet.', link };
  }
  return { key, label, severity: 'ok', status: 'Ready', detail: `Canonical route ready via spaceId ${channel.spaceId}.`, link };
};

const summarizeProvision = (result) => {
  if (!result) return { severity: 'muted', title: 'Provision not attempted', detail: '' };
  if (!result.success) {
    return { severity: 'error', title: 'Provision unavailable', detail: result.message || 'Provision failed.' };
  }

  const provisionedCount = Array.isArray(result.provisioned) ? result.provisioned.length : 0;
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];
  const createAccountRequired = skipped.some((item) => item?.reason === 'create_account_required');

  if (createAccountRequired) {
    return { severity: 'warn', title: 'Account setup required', detail: 'User needs a ComSapp account before provisioning can complete.' };
  }

  if (provisionedCount > 0) {
    return { severity: 'ok', title: 'Provision checked', detail: `Provision verified for ${provisionedCount} channel${provisionedCount === 1 ? '' : 's'}.` };
  }

  return { severity: 'muted', title: 'No channels provisioned', detail: 'No matching ComSapp channels are available for this user role.' };
};

const buildSpaceUrl = (channel, baseUrl) => {
  if (!channel?.enabled || !baseUrl || !channel?.spaceId) return '';
  return `${baseUrl.replace(/\/$/, '')}/spaces/${channel.spaceId}`;
};

const ComsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [config, setConfig] = useState(null);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [provisionStatus, setProvisionStatus] = useState(null);

  const loadComsHub = async () => {
    setLoading(true);
    setError('');
    setErrorCode('');
    try {
      const configRes = await api.get('/api/connector/config');
      setConfig(configRes?.data?.config || null);
      setDefaultComsappBaseUrl(configRes?.data?.defaultComsappBaseUrl || '');
      const provisionRes = await api.post('/api/connector/provision-me')
        .then((res) => res?.data || { success: false, message: 'No response from provision endpoint.' })
        .catch((err) => ({ success: false, message: err.response?.data?.message || 'Provision failed.' }));
      setProvisionStatus(summarizeProvision(provisionRes));
    } catch (err) {
      const code = err.response?.data?.code || '';
      const message = err.response?.data?.message || 'Could not load communications settings.';
      setErrorCode(code);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComsHub();
  }, []);

  const baseUrl = (config?.comsappBaseUrl || defaultComsappBaseUrl || '').replace(/\/$/, '');
  const staffUrl = buildSpaceUrl(config?.channels?.staff, baseUrl);
  const devUrl = buildSpaceUrl(config?.channels?.dev, baseUrl);
  const channelStatus = [
    getChannelStatus(config?.channels?.staff, 'staff', baseUrl),
    getChannelStatus(config?.channels?.dev, 'dev', baseUrl),
  ];

  return (
    <div style={{ padding: '28px 32px', maxWidth: 960, margin: '0 auto', color: '#e2e8f0' }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>Coms Hub</h2>
      <p style={{ color: '#94a3b8', marginTop: 8, marginBottom: 20 }}>
        Unified communications entry point for staff collaboration and dev handoff.
      </p>

      {loading && <p style={{ color: '#94a3b8' }}>Loading Coms Hub...</p>}
      {error && <ComsErrorBanner code={errorCode} message={error} onRetry={loadComsHub} />}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ background: '#111827', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>Available Spaces</div>
            {provisionStatus && (
              <div style={statusCardStyle(provisionStatus.severity)}>
                <strong>{provisionStatus.title}</strong>
                {provisionStatus.detail && <div style={{ marginTop: 4 }}>{provisionStatus.detail}</div>}
              </div>
            )}
            <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
              {channelStatus.map((item) => (
                <div key={item.key} style={statusCardStyle(item.severity)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <strong>{item.label}</strong>
                    <span>{item.status}</span>
                  </div>
                  <div style={{ marginTop: 4 }}>{item.detail}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {staffUrl && (
                <a href={staffUrl} target="_blank" rel="noreferrer" style={linkStylePrimary}>
                  Open Staff Space
                </a>
              )}
              {devUrl && (
                <a href={devUrl} target="_blank" rel="noreferrer" style={linkStyleSecondary}>
                  Open Dev Space
                </a>
              )}
              {!staffUrl && !devUrl && (
                <span style={{ color: '#94a3b8', fontSize: 13 }}>
                  No channels are enabled yet. Verify connector settings.
                </span>
              )}
            </div>
          </div>

          {RSMIN_APP_URL && (
            <div style={{ background: '#111827', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>Cross-Platform</div>
              <a href={`${RSMIN_APP_URL.replace(/\/$/, '')}/coms-hub`} target="_blank" rel="noreferrer" style={linkStyleGhost}>
                Open RS Coms Hub
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const linkStyleBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 12px',
  borderRadius: 8,
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: 13,
};

const linkStylePrimary = {
  ...linkStyleBase,
  background: '#0ea5e9',
  color: '#082f49',
};

const linkStyleSecondary = {
  ...linkStyleBase,
  background: 'transparent',
  border: '1px solid #0ea5e9',
  color: '#7dd3fc',
};

const linkStyleGhost = {
  ...linkStyleBase,
  background: 'transparent',
  border: '1px solid #64748b',
  color: '#cbd5e1',
};

const statusCardStyle = (severity) => {
  const palette = {
    ok: { background: '#052e16', border: '#166534', color: '#dcfce7' },
    warn: { background: '#451a03', border: '#b45309', color: '#fde68a' },
    error: { background: '#450a0a', border: '#b91c1c', color: '#fecaca' },
    muted: { background: '#0f172a', border: '#334155', color: '#cbd5e1' },
  };

  const current = palette[severity] || palette.muted;
  return {
    background: current.background,
    border: `1px solid ${current.border}`,
    color: current.color,
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 13,
  };
};

export default ComsPage;
